// ═══════════════════════════════════════════════════════════════════════════
// grainCommitment.js — PHYSICAL COMMITMENT vs. PRICE PROTECTION, split from
// the single `contractedBu`/`pricedBu` pair grainRules.js's
// summarizeGrainContracts() already computes (Kevin, 2026-08-19 overnight
// watchdog: "Do not display `contracted / priced` as interchangeable
// concepts... HTA/futures hedge can protect price without fixing basis.
// Bushels-only/physical commitment can constrain logistics without fully
// pricing.").
//
// Sourced from grain_contracts.contract_type's real, measured 4-value set
// (tools/position-fixture.json §grain_contracts._traps, book-wide SELECT
// DISTINCT 2026-08-17): "Fixed Price" 398 rows, "Basis" 47, "HTA" 13,
// "Bushels Only" 2, plus null (never captured / not one of the four). No
// other values exist in the live book — this file does not invent a fifth.
//
// PHYSICAL COMMITMENT = a delivery obligation exists (bushels_contracted >
// 0), REGARDLESS of what's priced. Every contract type creates one.
//
// PRICE PROTECTION, per bushel, is derived from TWO already-real facts on
// each row, never one collapsed "priced" bucket:
//   - bushels_priced (cash-locked — reused verbatim from
//     summarizeGrainContracts, never re-derived) closes ALL price risk on
//     those bushels.
//   - for the UNPRICED remainder (contracted - priced) on a row, the VEHICLE
//     (contract_type) says which leg, if any, is still locked:
//       HTA           -> futures leg locked, basis still open
//                         (position-fixture.json: "futures_price 5.12...
//                         cash_price a REAL zero... must not enter the
//                         weighted average price" — the futures number is
//                         real and locked, only cash/basis is undetermined).
//       Basis         -> basis leg locked, futures still open (the trap
//                         note: "Basis rows carry basis + cash_price with
//                         futures_price NULL" for a fully-priced row; for a
//                         PARTIALLY priced Basis row the unpriced remainder
//                         has the same basis-locked/futures-open shape).
//       Bushels Only  -> NEITHER leg locked — a genuine, unprotected
//                         physical commitment (position-fixture.json: "no
//                         futures leg either, cash_price a REAL zero").
//       Fixed Price   -> a Fixed Price row with an unpriced remainder is a
//                         data anomaly (the type implies fully priced) —
//                         treated as UNKNOWN protection, never assumed
//                         either way.
//       null/other    -> UNKNOWN protection — never assumed protected.
//
// priceProtectedBu (this file's one scalar for grainTargetPosition.js's
// range anchor) = cashPricedBu + futuresProtectedBu — the bushels whose
// FUTURES-direction price risk is closed, whether by a locked cash price or
// a locked-futures/open-basis HTA. basisProtectedBu (basis locked, futures
// still open) is tracked SEPARATELY and deliberately NOT folded into
// priceProtectedBu: a Basis contract leaves the farm fully exposed to
// futures direction, which is the risk a "protect futures" action addresses
// (see grainSaleReason.js's componentGuidance, item 3 of the 2026-08-19
// trigger — "strong futures + ordinary/unknown basis => protect futures /
// leave basis open" is a claim about closing FUTURES risk specifically).
//
// PURE — no fetch, no React, no Supabase.
// ═══════════════════════════════════════════════════════════════════════════

export const CONTRACT_TYPE = {
  FIXED_PRICE: "Fixed Price",
  BASIS: "Basis",
  HTA: "HTA",
  BUSHELS_ONLY: "Bushels Only",
};

// Duplicated from grainPosition.js's cropMatches() — NOT imported. Importing
// it would create a two-file import cycle the same way grainAction.js's own
// header already documents avoiding for this exact pair (grainPosition.js
// will import summarizeGrainCommitment FROM this file), and tools/_loadEs.js
// (the plain-Node ES-module loader every checker in this repo uses) has no
// circular-import support. Four lines, kept byte-identical to the original.
function cropMatches(rowCrop, targetCrop) {
  const u = String(rowCrop || "").toUpperCase();
  const t = String(targetCrop || "").toUpperCase();
  return t === "WHEAT" ? (u === "WHEAT" || u === "WINTER WHEAT") : u === t;
}

export function classifyRowProtection(contractType, unpricedBu) {
  if (!(unpricedBu > 0)) return { futuresProtected: 0, basisProtected: 0, physicalOnly: 0, unknown: 0 };
  switch (contractType) {
    case CONTRACT_TYPE.HTA:
      return { futuresProtected: unpricedBu, basisProtected: 0, physicalOnly: 0, unknown: 0 };
    case CONTRACT_TYPE.BASIS:
      return { futuresProtected: 0, basisProtected: unpricedBu, physicalOnly: 0, unknown: 0 };
    case CONTRACT_TYPE.BUSHELS_ONLY:
      return { futuresProtected: 0, basisProtected: 0, physicalOnly: unpricedBu, unknown: 0 };
    default:
      // Fixed Price with an unpriced remainder (anomaly), null, or any
      // future/unrecognized label — never assumed protected or unprotected.
      return { futuresProtected: 0, basisProtected: 0, physicalOnly: 0, unknown: unpricedBu };
  }
}

// summarizeGrainCommitment(contractRows, crop, cropYear) — the SAME
// crop_year/cropMatches filter and zero-bushel-row exclusion (trap 2) as
// summarizeGrainContracts(), so the two functions always agree on WHICH rows
// count; this one only splits what those rows mean by protection type.
export function summarizeGrainCommitment(contractRows, crop, cropYear) {
  const rows = (contractRows || []).filter(r =>
    Number(r.crop_year) === Number(cropYear) && cropMatches(r.crop, crop));

  let physicalBu = 0, cashPricedBu = 0, futuresProtectedBu = 0, basisProtectedBu = 0,
    physicalOnlyBu = 0, unknownProtectionBu = 0, rowCount = 0;
  const byContractType = {};

  for (const r of rows) {
    const contracted = Number(r.bushels_contracted) || 0;
    if (!(contracted > 0)) continue; // trap 2 — same exclusion as summarizeGrainContracts
    rowCount++;
    physicalBu += contracted;
    const priced = r.bushels_priced != null ? Number(r.bushels_priced) || 0 : 0;
    const cashPriced = Math.min(Math.max(0, priced), contracted); // never over-credit a row
    cashPricedBu += cashPriced;
    const unpriced = Math.max(0, contracted - cashPriced);
    const type = r.contract_type || null;
    const key = type || "unknown";
    byContractType[key] = (byContractType[key] || 0) + contracted;
    const split = classifyRowProtection(type, unpriced);
    futuresProtectedBu += split.futuresProtected;
    basisProtectedBu += split.basisProtected;
    physicalOnlyBu += split.physicalOnly;
    unknownProtectionBu += split.unknown;
  }

  if (!rows.length) {
    // Unknown-vs-zero, the SAME convention summarizeGrainContracts follows:
    // zero grain_contracts rows on file means "never captured," not
    // "confirmed nothing committed."
    return {
      physicalBu: null, cashPricedBu: null, futuresProtectedBu: null,
      basisProtectedBu: null, physicalOnlyBu: null, unknownProtectionBu: null,
      priceProtectedBu: null, byContractType: {}, rowCount: 0,
    };
  }

  return {
    physicalBu, cashPricedBu, futuresProtectedBu, basisProtectedBu,
    physicalOnlyBu, unknownProtectionBu,
    priceProtectedBu: cashPricedBu + futuresProtectedBu,
    byContractType, rowCount,
  };
}

// pctOfExpected — small formatting helper shared by grainPosition.js /
// grainAction.js so a percentage is never hand-rolled three different ways.
// Returns null (not 0) when either input is unknown.
export function pctOfExpected(bu, expectedBu) {
  if (bu == null || expectedBu == null || !(expectedBu > 0)) return null;
  return +(bu / expectedBu * 100).toFixed(1);
}
